import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiClient } from '@/lib/api/client'
import type { HelmUser, HelmUsersResponse } from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { useAsyncData } from '@/src/hooks/useAsyncData'

function userDisplayName(user: HelmUser) {
  return user.display_name || user.name || user.email
}

function userRoles(user: HelmUser) {
  return user.helm_roles.map((role) => role.role)
}

/** Staff can own work; a customer's login cannot. */
export function isAssignableOwner(user: HelmUser) {
  const roles = userRoles(user)
  return roles.length === 0 || roles.some((role) => role !== 'customer')
}

function userSubtext(user: HelmUser) {
  const roles = userRoles(user).join(', ') || 'staff'
  const displayName = userDisplayName(user)
  return [displayName !== user.email ? user.email : null, roles].filter(Boolean).join(' · ')
}

function sortUsersByEmail(first: HelmUser, second: HelmUser) {
  return first.email.localeCompare(second.email)
}

export function HelmOwnerCombobox({
  id,
  value,
  onValueChange,
  disabled,
  placeholder = 'Search by name or email',
}: {
  id?: string
  value: string
  onValueChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const { data, loading, error } = useAsyncData(async () => apiClient.listHelmUsers(), [])
  const users = useMemo(
    () => (data?.users ?? []).filter(isAssignableOwner).sort(sortUsersByEmail),
    [data],
  )
  const selectedUser =
    users.find((user) => user.id === value) ?? data?.users.find((user) => user.id === value) ?? null
  const filteredUsers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return users

    return users.filter((user) =>
      [user.email, user.name, user.display_name, user.profile_role, userRoles(user).join(' ')].some(
        (field) =>
          String(field || '')
            .toLowerCase()
            .includes(normalizedQuery),
      ),
    )
  }, [query, users])

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setQuery('')
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="field"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between"
        >
          <span
            className={cn('min-w-0 truncate text-left', !selectedUser && 'text-muted-foreground')}
            title={selectedUser?.email}
          >
            {selectedUser ? (
              // The person by name; the email after it, as far as there is room.
              <>
                {userDisplayName(selectedUser)}
                {userDisplayName(selectedUser) !== selectedUser.email && (
                  <span className="ml-1.5 text-muted-foreground">{selectedUser.email}</span>
                )}
              </>
            ) : value ? (
              `Unknown user (${value.slice(0, 8)}...)`
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <div role="listbox" className="max-h-72 overflow-y-auto p-1">
          {value && (
            <button
              type="button"
              role="option"
              aria-selected={!value}
              className="flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              onClick={() => {
                onValueChange('')
                setOpen(false)
                setQuery('')
              }}
            >
              <Check className="mt-0.5 h-4 w-4 opacity-0" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">Unassigned</span>
                <span className="block truncate text-xs text-muted-foreground">
                  Clear the owner for this consignment.
                </span>
              </span>
            </button>
          )}
          {loading ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              Loading Helm users...
            </div>
          ) : error ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              Unable to load Helm users.
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              No Helm users match that.
            </div>
          ) : (
            filteredUsers.map((user) => {
              const selected = user.id === value
              return (
                <button
                  key={user.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className="flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  onClick={() => {
                    onValueChange(user.id)
                    setOpen(false)
                    setQuery('')
                  }}
                >
                  <Check className={cn('mt-0.5 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{userDisplayName(user)}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {userSubtext(user)}
                    </span>
                  </span>
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/** Who owns a record, by name, with their email on hover. */
export function HelmOwnerName({
  ownerUserId,
  className,
}: {
  ownerUserId?: string | null
  className?: string
}) {
  const { data, loading } = useAsyncData<HelmUsersResponse>(
    async () => (ownerUserId ? apiClient.listHelmUsers() : { users: [], invitations: [] }),
    [ownerUserId],
  )
  const owner = data?.users.find((user) => user.id === ownerUserId) ?? null

  if (!ownerUserId) {
    return <p className={cn('mt-1 font-medium', className)}>Unassigned</p>
  }

  if (loading) {
    return <p className={cn('mt-1 text-sm text-muted-foreground', className)}>Loading owner...</p>
  }

  return (
    <p className={cn('mt-1 truncate font-medium', className)} title={owner?.email || ownerUserId}>
      {owner ? userDisplayName(owner) : ownerUserId}
    </p>
  )
}
